import {
    type FormEvent,
    useCallback,
    useEffect,
    useRef,
    useState,
} from "react";
import { type FileRejection, useDropzone } from "react-dropzone";
import * as Sentry from "@sentry/react";
import type { AxiosProgressEvent } from "axios";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";

import {
    getPresignedUrl,
    getApiErrorMessage,
    type IApiAxiosError,
    type OutputTemplateListItem,
    uploadToStorage,
} from "@/api";
import { Button } from "@/components/ui/button";
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from "@/components/ui/dialog";

import { UploadDropzone } from "../upload/UploadDropzone";
import { UploadFileList } from "../upload/UploadFileList";
import type { FileUploadItem } from "../upload/types";
import { useReplaceTemplate, useUploadTemplate } from "./useOutputTemplates";
import {
    TEMPLATE_ACCEPT,
    TEMPLATE_MAX_FILE_BYTES,
    TEMPLATE_MAX_FILE_MB,
    TEMPLATE_REJECTION_COPY,
} from "./templateUploadConfig";

interface UploadTemplateDialogProps {
    open: boolean;
    onClose: () => void;
    /** When set, the file replaces this template's current version. */
    templateId?: number;
    onSubmitted?: (template: OutputTemplateListItem) => void;
    initialFiles?: File[];
}

export function UploadTemplateDialog({
    open,
    onClose,
    templateId,
    onSubmitted,
    initialFiles,
}: UploadTemplateDialogProps) {
    const isReplace = templateId != null;
    const [files, setFiles] = useState<FileUploadItem[]>([]);
    const [error, setError] = useState<string | null>(null);
    const [submitting, setSubmitting] = useState(false);
    const nextIdRef = useRef(0);
    const initialFilesConsumedRef = useRef(false);

    const uploadMutation = useUploadTemplate();
    const replaceMutation = useReplaceTemplate(templateId ?? -1);
    const activeMutation = isReplace ? replaceMutation : uploadMutation;
    const isAnyUploading = files.some(
        (file) => file.status === "uploading" && !file.objectKey
    );
    const confirmableCount = files.filter(
        (file) =>
            file.objectKey &&
            (file.status === "ready" || file.status === "failed")
    ).length;

    const setRow = useCallback(
        (id: string, patch: Partial<FileUploadItem>) =>
            setFiles((prev) =>
                prev.map((f) => (f.id === id ? { ...f, ...patch } : f))
            ),
        []
    );

    const uploadToTemporaryStorage = useCallback(
        async (item: FileUploadItem) => {
            let phase: "presign" | "transfer" = "presign";
            try {
                const presigned = await getPresignedUrl({
                    filename: item.file.name,
                    content_type: item.file.type,
                });
                phase = "transfer";
                const objectKey = await uploadToStorage(
                    item.file,
                    presigned,
                    (event: AxiosProgressEvent) => {
                        const progress = event.total
                            ? Math.round((event.loaded / event.total) * 100)
                            : Math.round((event.progress ?? 0) * 100);
                        setRow(item.id, { progress });
                    }
                );
                setRow(item.id, {
                    objectKey,
                    progress: 100,
                    status: "ready",
                });
            } catch (err) {
                Sentry.captureException(err, {
                    tags: { feature: "template-upload", phase },
                    extra: {
                        fileName: item.file.name,
                        byteSize: item.file.size,
                    },
                });
                setRow(item.id, {
                    status: "failed",
                    errorReason:
                        phase === "presign"
                            ? "Couldn't start the upload. Try again."
                            : "Couldn't upload to storage. Try again.",
                });
            }
        },
        [setRow]
    );

    const onDrop = useCallback(
        (accepted: File[]) => {
            if (accepted.length === 0) return;
            const rows: FileUploadItem[] = accepted.map((file) => ({
                id: String(nextIdRef.current++),
                file,
                objectKey: null,
                progress: 0,
                status: "uploading" as const,
            }));
            setFiles((prev) =>
                isReplace ? rows.slice(0, 1) : [...prev, ...rows]
            );
            setError(null);
            rows.slice(0, isReplace ? 1 : rows.length).forEach(
                uploadToTemporaryStorage
            );
        },
        [isReplace, uploadToTemporaryStorage]
    );

    useEffect(() => {
        if (!open) {
            initialFilesConsumedRef.current = false;
            return;
        }
        if (initialFiles?.length && !initialFilesConsumedRef.current) {
            initialFilesConsumedRef.current = true;
            onDrop(initialFiles);
        }
    }, [open, initialFiles, onDrop]);

    const onDropRejected = useCallback((rejections: FileRejection[]) => {
        const tooLarge = rejections.some((r) =>
            r.errors.some((e) => e.code === "file-too-large")
        );
        setError(
            tooLarge
                ? `Templates must be ${TEMPLATE_MAX_FILE_MB}MB or smaller`
                : TEMPLATE_REJECTION_COPY
        );
    }, []);

    const { getRootProps, getInputProps, isDragActive } = useDropzone({
        onDrop,
        onDropRejected,
        accept: TEMPLATE_ACCEPT,
        maxSize: TEMPLATE_MAX_FILE_BYTES,
        multiple: !isReplace,
        disabled: submitting,
    });

    const handleRemove = useCallback(
        (id: string) => setFiles((prev) => prev.filter((f) => f.id !== id)),
        []
    );

    const reset = () => {
        setFiles([]);
        setError(null);
        setSubmitting(false);
    };

    const handleClose = () => {
        if (submitting) return;
        reset();
        onClose();
    };

    const handleSubmit = async (event: FormEvent) => {
        event.preventDefault();
        const pending = files.filter(
            (f) =>
                f.objectKey && (f.status === "ready" || f.status === "failed")
        );
        if (pending.length === 0) {
            setError("Add a .xlsx or .pdf template to upload");
            return;
        }
        setError(null);
        setSubmitting(true);

        let created = 0;
        let anyFailed = false;
        for (const item of pending) {
            setRow(item.id, {
                status: "uploading",
                errorReason: undefined,
            });
            try {
                const template = await activeMutation.mutateAsync({
                    payload: {
                        object_name: item.objectKey!,
                        filename: item.file.name,
                    },
                });
                created += 1;
                onSubmitted?.(template);
                setFiles((prev) => prev.filter((f) => f.id !== item.id));
            } catch (err) {
                anyFailed = true;
                setRow(item.id, {
                    status: "failed",
                    errorReason: getApiErrorMessage(
                        err as IApiAxiosError,
                        "Upload failed"
                    ),
                });
            }
        }

        setSubmitting(false);
        if (anyFailed) return; // stay open; each failed row carries its reason

        toast.success(
            isReplace
                ? "Replacement uploaded"
                : created === 1
                  ? "Template uploaded"
                  : `${created} templates uploaded`,
            {
                description: isReplace
                    ? "The current version keeps serving while the replacement compiles."
                    : "Compiling in the background.",
            }
        );
        handleClose();
    };

    return (
        <Dialog
            open={open}
            onOpenChange={(next) => {
                if (!next) handleClose();
            }}
        >
            <DialogContent className="flex max-h-[85dvh] flex-col gap-0 overflow-hidden p-0 sm:max-w-2xl">
                <DialogHeader className="border-b p-4 pr-8">
                    <DialogTitle>
                        {isReplace ? "Replace template" : "Upload template"}
                    </DialogTitle>
                    <DialogDescription>
                        {isReplace
                            ? "Upload a new copy. The current version keeps serving until the replacement is ready."
                            : "Upload a spreadsheet or PDF. Polly fills a fresh copy from any conversation."}
                    </DialogDescription>
                </DialogHeader>
                <form
                    onSubmit={handleSubmit}
                    className="flex min-h-0 flex-1 flex-col"
                >
                    <div className="flex min-h-0 flex-1 flex-col gap-3 overflow-y-auto p-4">
                        <UploadDropzone
                            getRootProps={getRootProps}
                            getInputProps={getInputProps}
                            isDragActive={isDragActive}
                            idleLabel={
                                isReplace
                                    ? "Drop a .xlsx or .pdf here, or click to browse"
                                    : "Drop .xlsx or .pdf templates here, or click to browse"
                            }
                            hint={`Spreadsheet or PDF up to ${TEMPLATE_MAX_FILE_MB}MB each`}
                        />

                        <UploadFileList
                            items={files}
                            onRemove={handleRemove}
                            removeDisabled={submitting}
                        />

                        {error && (
                            <p
                                data-testid="template-upload-error"
                                className="text-destructive text-sm"
                            >
                                {error}
                            </p>
                        )}
                    </div>

                    <DialogFooter className="mx-0 mb-0">
                        <Button
                            type="button"
                            variant="outline"
                            onClick={handleClose}
                            disabled={submitting}
                        >
                            Cancel
                        </Button>
                        <Button
                            type="submit"
                            data-testid="template-upload-submit"
                            disabled={
                                submitting ||
                                isAnyUploading ||
                                confirmableCount === 0
                            }
                        >
                            {submitting && (
                                <Loader2
                                    className="mr-2 size-4 animate-spin"
                                    aria-hidden
                                />
                            )}
                            {isReplace ? "Replace" : "Upload"}
                        </Button>
                    </DialogFooter>
                </form>
            </DialogContent>
        </Dialog>
    );
}
